import { createStoreon, StoreonModule } from 'storeon';
import { useStoreon } from 'storeon/react';
//import { persistState } from '@storeon/localstorage';

import {
  Country,
  County,
  Page,
  PhoneData,
  PhoneModel,
  PromoDeviceProps,
  PromoEvents,
  PurchasedPhone,
  Retailer,
  StepType,
  StepValidationMap,
  Vendor,
} from './types';
import {
  getCounties,
  getCountries,
  getEligibleDevices,
  getEvents,
  getPhoneModels,
  getPromoDevices,
  getRecaptchaKey,
  getRetailers,
  getVendors,
  placeOrder,
  validateImei,
} from './network';
import { validateStep } from './validation';
import { getPurchasedDate } from '../utils/getPurchasedDate';
import { config } from './config';

// State structure
export interface State {
  step: StepType;
  page: Page;
  requestSent: boolean;
  imei: string;
  isImeiError: boolean;
  isValidImei: boolean;
  imeiOld: string;
  orderId: string;
  orderErrorMessage: string;
  orderErrors: Record<string, string[]>;
  checkErrorMessage: string;
  checkErrors: Record<string, string[]>;
  brand: string;
  model: string;
  tradeInValue: string;
  tradeInValueBad: string;
  email: string;
  confirmEmail: string;
  status: string;
  firstName: string;
  lastName: string;
  emailForContact: string;
  postalCode: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  county: string;
  country: string;
  retailer: string;
  retailerOther: string;
  iban: string;
  isDeviceWorking: boolean;
  isVendorUnlocked: boolean;
  isFactoryResetDone: boolean;
  isTermsConfirmed: boolean;
  retailers: ReadonlyArray<Retailer>;
  countries: ReadonlyArray<Country>;
  counties: ReadonlyArray<County>;
  brands: ReadonlyArray<Vendor>;
  modelsMyBrand: ReadonlyArray<PhoneModel>;
  generalError: Error | string | null;
  breadcrumbsValidation: StepValidationMap;
  purchaseDate: number | null; // timestamp via new Date().getTime();
  purchaseConfirmation: string | null;
  purchaseConfirmationName: string;
  purchasedModel: string | null;
  purchasedPhone: PurchasedPhone | null;
  recaptchaKey: string;
  recaptchaToken: string;
  promoDevices: PromoDeviceProps[] | [];
  promoDeviceCount: number;
  eligibleDevices: PhoneData[] | [];
  tradeinValueFinal: number;
  promotions: PromoEvents;
}

// Events declaration: map of event names to type of event data
interface StoreEvents {
  // event which doesnt go with any data
  none: undefined;
  // `step` event which goes with step as data
  step: StepType;
  confirmOrder: undefined;
  validateSteps: undefined;
  retryImei: undefined;
  clear: undefined;
  update: Partial<State>;
  updateState: Partial<State>;
  recaptchaKey: undefined;
  vendorsList: undefined;
  phonesByVendor: string;
  countriesList: undefined;
  countiesList: undefined;
  retailersList: undefined;
  eligibleDevices: undefined;
  promoDevices: undefined;
  promoList: undefined;
}

const initState: State = {
  page: Page.Home,
  step: StepType.NewDeviceCheck,
  requestSent: false,
  orderId: '',
  orderErrorMessage: '',
  orderErrors: {},
  checkErrors: {},
  checkErrorMessage: '',
  imei: '',
  isImeiError: false,
  isValidImei: false,
  imeiOld: '',
  brand: '',
  model: '',
  tradeInValue: '0',
  tradeInValueBad: '0',
  email: '',
  confirmEmail: '',
  status: '',
  firstName: '',
  lastName: '',
  emailForContact: '',
  postalCode: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  county: '',
  country: 'IE',
  retailer: '',
  retailerOther: '',
  iban: '',
  isVendorUnlocked: false,
  isFactoryResetDone: false,
  isTermsConfirmed: false,
  isDeviceWorking: false,
  retailers: [],
  countries: [],
  counties: [],
  brands: [],
  modelsMyBrand: [],
  generalError: null,
  breadcrumbsValidation: {
    [StepType.NewDeviceCheck]: true,
    [StepType.OldDeviceModel]: false,
    [StepType.OldDeviceStatus]: false,
    [StepType.Details]: false,
    [StepType.Confirmation]: false,
  } as Record<Partial<StepType>, boolean>,
  purchaseDate: null,
  purchaseConfirmation: null,
  purchaseConfirmationName: '',
  purchasedModel: null,
  purchasedPhone: null,
  recaptchaKey: '',
  recaptchaToken: '',
  promoDevices: [],
  promoDeviceCount: 0,
  eligibleDevices: [],
  tradeinValueFinal: 0,
  promotions: {
    is_active: false,
    is_transactional: false,
    is_enhancement_active: false,
    is_eligible_devices_enhancement_active: false,
    purchase_date_period: {
      start: '',
      end: '',
    },
    promotion_date_period: {
      start: '',
      end: '',
    },
    claim_date_period: {
      start: '',
      end: '',
    },
  },
};

function createErrorHandler() {
  let errorTimeout: number | undefined;
  return (err: Error, timeout = 5000) => {
    store.dispatch('update', {
      generalError: err,
    });
    window.clearTimeout(errorTimeout);
    errorTimeout = window.setTimeout(() => {
      store.dispatch('update', {
        generalError: null,
      });
    }, timeout);
  };
}

const showErrorForPeriod = createErrorHandler();

const stepsModule: StoreonModule<State, StoreEvents> = (store) => {
  store.on('@init', () => {
    return { ...initState };
  });
  store.on('recaptchaKey', () => {
    getRecaptchaKey()
      .then((key) => {
        store.dispatch('update', {
          recaptchaKey: key,
        });
      })
      .catch((err) => {
        showErrorForPeriod(err);
      });
  });
  store.on('vendorsList', () => {
    getVendors()
      .then((vendors) => {
        store.dispatch('update', {
          brands: vendors.map((vendor, index) => ({ value: `${index}`, label: vendor })),
        });
      })
      .catch((err) => {
        showErrorForPeriod(err);
      });
  });
  store.on('eligibleDevices', () => {
    getEligibleDevices()
      .then((devices) => {
        store.dispatch('update', {
          eligibleDevices: devices,
        });
      })
      .catch((err) => {
        showErrorForPeriod(err);
      });
  });
  store.on('promoDevices', () => {
    getPromoDevices()
      .then((phones) => {
        store.dispatch('update', {
          promoDeviceCount: phones.length,
          promoDevices: phones,
        });
      })
      .catch((err) => {
        showErrorForPeriod(err);
      });
  });
  store.on('promoList', () => {
    getEvents()
      .then((promotions) => {
        store.dispatch('update', {
          promotions,
        });
        if (promotions.is_active) {
          store.dispatch('update', {
            step: StepType.NewDeviceCheck,
          });
        }
      })
      .catch((err) => {
        showErrorForPeriod(err);
      });
  });
  store.on('countriesList', () => {
    getCountries()
      .then((countries) => {
        store.dispatch('update', {
          countries,
          country: 'IE',
        });
      })
      .catch((err) => {
        showErrorForPeriod(err);
      });
  });
  store.on('countiesList', () => {
    getCounties()
      .then((counties) => {
        store.dispatch('update', {
          counties,
        });
      })
      .catch((err) => {
        showErrorForPeriod(err);
      });
  });
  store.on('retailersList', () => {
    getRetailers()
      .then((retailers) => {
        store.dispatch('update', {
          retailers: retailers.map((item) => ({ value: item.toLowerCase(), label: item })),
        });
      })
      .catch((err) => {
        showErrorForPeriod(err);
      });
  });
  store.on('phonesByVendor', (state, vendorName: string) => {
    if (vendorName === 'Other') {
      store.dispatch('update', {
        modelsMyBrand: [],
      });
      return;
    }

    getPhoneModels(vendorName, state.imei, state.purchaseDate)
      .then((phones) => {
        store.dispatch('update', {
          modelsMyBrand: phones.map((phone) => ({
            value: `${phone.id}`,
            label: phone.model,
            priceMin: phone.bad_price,
            priceMax: phone.good_price,
            pricePromo: phone.promo_price,
          })),
        });
        if (vendorName === 'Any Other Smartphone') {
          store.dispatch('update', {
            model: `${phones[0].id}`,
          });
        }
      })
      .catch((err) => {
        showErrorForPeriod(err);
      });
  });
  store.on('step', (state, step) => {
    if (step && !state.requestSent) {
      if (step === StepType.Confirmation) {
        store.dispatch('validateSteps');
        store.dispatch('confirmOrder');
      } else {
        if (state.step === StepType.NewDeviceCheck) {
          store.dispatch('recaptchaKey');
          store.dispatch('update', {
            requestSent: true,
            isValidImei: false,
            purchasedModel: null,
            checkErrors: {},
            checkErrorMessage: '',
          });
          validateImei<{ imei: string; purchased_date: string; g_recaptcha_response: string; purchasedDevice: string }>(
            {
              imei: state.imei,
              purchased_date: getPurchasedDate(state.purchaseDate),
              g_recaptcha_response: state.recaptchaToken,
              purchasedDevice: state.purchasedPhone ? state.purchasedPhone : 'NoDevice',
            }
          )
            .then((response) => {
              if (response.message === true) {
                store.dispatch('update', {
                  step: step,
                  // TODO: VOV: Review before Trade in start (TP-324)
                  /*purchasedPhone:
                    response.model === 'DM1'
                      ? PurchasedPhone.DM1
                      : response.model === 'DM2'
                      ? PurchasedPhone.DM2
                      : response.model === 'DM3'
                      ? PurchasedPhone.DM3
                      : null,
                  */
                  purchasedModel: response.data.full_name,
                  purchasedPhone: response.data.alias,
                  requestSent: false,
                  isValidImei: true,
                });
                if (state.brand) {
                  store.dispatch('phonesByVendor', state.brand);
                }
              } else if (response.errors?.g_recaptcha_response) {
                store.dispatch('update', {
                  checkErrors: {},
                  checkErrorMessage: response.errors?.g_recaptcha_response[0],
                  requestSent: false,
                });
              } else if (response.responseStatus === 429 && typeof response.message === 'string') {
                store.dispatch('update', {
                  checkErrors: {},
                  checkErrorMessage: response.message,
                  requestSent: false,
                });
              } else {
                if (typeof response.message === 'string' && !response.errors?.imei) {
                  store.dispatch('update', {
                    checkErrors: response.errors,
                    checkErrorMessage: response.message,
                    requestSent: false,
                  });
                } else {
                  store.dispatch('updateState', {
                    isImeiError: true,
                    requestSent: false,
                  });
                }
              }
            })
            .catch(() => {
              store.dispatch('updateState', {
                isImeiError: true,
                requestSent: false,
              });
            });
        } else {
          store.dispatch('update', {
            step: step,
          });
        }
      }
    }
  });
  store.on('retryImei', () => {
    store.dispatch('updateState', {
      isImeiError: false,
    });
  });
  store.on('confirmOrder', (state) => {
    if (state.breadcrumbsValidation[StepType.Confirmation] && !state.requestSent) {
      store.dispatch('update', {
        requestSent: true,
        orderErrors: {},
        orderErrorMessage: '',
      });

      const formData = {
        site: config.tradeInSource,

        device_id: state.model,
        device_reset: state.isFactoryResetDone ? '1' : '0',
        device_unlocked: state.isVendorUnlocked ? '1' : '0',
        device_working: state.isDeviceWorking ? '1' : '0',
        terms_accepted: state.isTermsConfirmed ? '1' : '0',
        sold_imei: state.imei,
        tradein_imei: state.imeiOld,
        tradein_value: state.tradeinValueFinal.toString(),

        first_name: state.firstName,
        last_name: state.lastName,
        email: state.email,
        address: `${state.addressLine1} ${state.addressLine2}`,
        postal_code: state.postalCode,
        country: state.country,
        county: state.county,
        city: state.city,

        iban: state.iban,

        retailer: state.retailer === 'Other' ? state.retailerOther : state.retailer,
        purchased_date: getPurchasedDate(state.purchaseDate),
        purchased_proof: state.purchaseConfirmation,
        purchased_device: state.purchasedPhone,

        g_recaptcha_response: state.recaptchaToken,
      };
      placeOrder<Partial<State>>({
        ...formData,
      })
        .then((response) => {
          if (response.success) {
            store.dispatch('update', {
              ...initState,
              step: StepType.Confirmation,
              page: Page.Steps,
              orderId: response.order_number,
              requestSent: false,
              recaptchaKey: state.recaptchaKey,
              recaptchaToken: state.recaptchaToken,
              brands: state.brands,
              counties: state.counties,
              countries: state.countries,
              retailers: state.retailers,
              county: state.county,
              promotions: state.promotions,
              promoDevices: state.promoDevices,
              eligibleDevices: state.eligibleDevices,
            });
          } else if (response.errors?.g_recaptcha_response) {
            store.dispatch('update', {
              orderErrors: {},
              orderErrorMessage: response.errors?.g_recaptcha_response[0],
              requestSent: false,
            });
          } else {
            let errors = {};
            let message = '';
            if (response.message) {
              message = response.message;
            }
            if (response.errors) {
              errors = response.errors;
            }
            if (!message) {
              message = 'An error appeared while tried to send an order.';
            }
            store.dispatch('update', {
              orderErrors: errors,
              orderErrorMessage: message,
              requestSent: false,
            });
          }
        })
        .catch((err) => {
          store.dispatch('update', {
            requestSent: false,
            orderErrors: {},
            orderErrorMessage: 'An error appeared while tried to send an order.',
          });
          showErrorForPeriod(err, 5000);
        });
    } else {
      showErrorForPeriod(new Error('Unknown error, try again'), 30000);
    }
  });
  store.on('validateSteps', (state) => {
    const isValidForNewDevice = validateStep(StepType.NewDeviceCheck, state) && state.isValidImei;
    const isValidForOldDeviceModel = validateStep(StepType.OldDeviceModel, state);
    const isValidForOldDeviceStatus = validateStep(StepType.OldDeviceStatus, state);
    const isValidForDetails = validateStep(StepType.Details, state);
    return {
      breadcrumbsValidation: {
        [StepType.NewDeviceCheck]: true, // First step is always available
        [StepType.OldDeviceModel]: isValidForNewDevice,
        [StepType.OldDeviceStatus]: isValidForOldDeviceModel && isValidForNewDevice,
        [StepType.Details]: isValidForOldDeviceStatus && isValidForOldDeviceModel && isValidForNewDevice,
        [StepType.Confirmation]:
          isValidForDetails && isValidForOldDeviceStatus && isValidForOldDeviceModel && isValidForNewDevice,
      } as Record<Partial<StepType>, boolean>,
    };
  });
  store.on('updateState', (state, partialState) => {
    return { ...state, ...partialState };
  });
  store.on('update', (state, partialState) => {
    store.dispatch('updateState', partialState);
    store.dispatch('validateSteps');
  });
  store.on('clear', () => {
    store.dispatch('updateState', {
      step: StepType.NewDeviceCheck,
      page: Page.Home,
      orderId: '',
    });
  });
};

/*const ignoredKeysForPersistence: Array<keyof State> = ['generalError', 'isImeiError'];
/**
 * Will save those state keys in localStorage
 *
const persistedKeys = (Object.keys(initState) as Array<keyof State>).filter(
  (key) => !ignoredKeysForPersistence.includes(key)
);
*/
export const store = createStoreon<State, StoreEvents>([stepsModule]);

export const useState = (...stateKeys: Array<keyof State>) => {
  return useStoreon<State, StoreEvents>(...stateKeys);
};
